<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { useOnline } from '@vueuse/core';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { ADDRESS_MAX, ADDRESS_MIN } from '@/domain/store-setup/location-step';
  import type { IGeoResult } from '@/interfaces/IStoreSetup';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * The typed address of the manual mode (plan §4.1): «Buscar dirección»
   * geocodes it on the server, restricted to the selected country. Reports
   * the result (null = not found) or a failure; shows the matching line.
   */
  type TGeocodeStatus = 'idle' | 'found' | 'notFound' | 'failed';

  const props = withDefaults(
    defineProps<{
      country: string;
      value: string;
      /** The API geocode of the page's place search (`usePlacesSearch().geocode`). */
      geocode: (address: string) => Promise<IGeoResult | null>;
      disabled?: boolean;
    }>(),
    { disabled: false }
  );
  const emit = defineEmits<{
    (e: 'input', address: string): void;
    (e: 'resolved', result: IGeoResult | null): void;
    (e: 'failed'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const online = useOnline();

  const inputId = nextDomId('setup-manual-address');
  const busy = ref(false);
  const status = ref<TGeocodeStatus>('idle');
  const foundAddress = ref('');
  const showError = ref(false);
  let alive = true;

  const tooShort = computed(() => props.value.trim().length < ADDRESS_MIN);
  const error = computed(() =>
    showError.value && tooShort.value
      ? tr('storeSetup.location.addressRequired')
      : undefined
  );

  function onInput(value: string) {
    emit('input', value);
    // The line about the previous search no longer describes what is typed.
    if (status.value !== 'idle') status.value = 'idle';
  }

  async function find() {
    if (busy.value || props.disabled || !online.value) return;
    if (tooShort.value) {
      showError.value = true;
      return;
    }
    busy.value = true;
    try {
      const result = await props.geocode(props.value.trim());
      if (!alive) return;
      status.value = result ? 'found' : 'notFound';
      foundAddress.value = result?.formattedAddress ?? '';
      emit('resolved', result);
    } catch {
      if (!alive) return;
      status.value = 'failed';
      emit('failed');
    } finally {
      if (alive) busy.value = false;
    }
  }

  // A result line of another country no longer applies.
  watch(
    () => props.country,
    () => {
      status.value = 'idle';
    }
  );

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <div class="v2-setup-manual-address">
    <PosUiField
      :label="$t('storeSetup.location.address')"
      :input-id="inputId"
      :error="error"
    >
      <PosUiInput
        :id="inputId"
        :value="value"
        :maxlength="ADDRESS_MAX"
        autocomplete="off"
        :disabled="disabled"
        @input="onInput"
        @enter="find"
        @blur="showError = true"
      />
    </PosUiField>
    <PosUiButton
      class="find-button"
      color="neutral"
      variant="outline"
      icon="magnify"
      :disabled="disabled || busy || !online"
      @click="find"
      >{{ $t('storeSetup.location.findAddress') }}</PosUiButton
    >
    <p v-if="status === 'found'" class="geocode-line is-found" role="status">
      <PosUiIcon name="check-circle" class="line-icon" />
      <span class="line-text">{{
        $t('storeSetup.location.found', { address: foundAddress })
      }}</span>
    </p>
    <p
      v-else-if="status === 'notFound'"
      class="geocode-line is-warning"
      role="status"
    >
      <PosUiIcon name="alert-outline" class="line-icon" />
      <span class="line-text">{{
        $t('storeSetup.location.geocodeNotFound')
      }}</span>
    </p>
    <div
      v-else-if="status === 'failed'"
      class="geocode-line is-error"
      role="alert"
    >
      <PosUiIcon name="alert-circle-outline" class="line-icon" />
      <span class="line-text">{{
        $t('storeSetup.location.geocodeFailed')
      }}</span>
      <button
        type="button"
        class="retry-link"
        :disabled="disabled || busy || !online"
        @click="find"
      >
        {{ $t('storeSetup.location.retry') }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-manual-address {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-3;
    .find-button {
      @apply tw-self-start;
    }
    .geocode-line {
      @apply tw-m-0 tw-flex tw-flex-wrap tw-items-start tw-gap-2 tw-text-sm;
      &.is-found {
        @apply tw-text-emerald-700;
      }
      &.is-warning {
        @apply tw-text-amber-700;
      }
      &.is-error {
        @apply tw-items-center tw-text-red-700;
      }
    }
    .line-icon {
      @apply tw-mt-0.5 tw-shrink-0 tw-text-base;
    }
    .line-text {
      @apply tw-min-w-0 tw-flex-1;
    }
    .retry-link {
      @apply tw-min-h-11 tw-border-0 tw-bg-transparent tw-px-1 tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400 tw-no-underline;
      }
    }
  }
</style>
